import {
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Content, Header, Page, Progress } from '@backstage/core-components';
import { Chip, IconButton, Link, List, ListItem, ListItemSecondaryAction, ListItemText, Tooltip, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import OpenInBrowserIcon from '@material-ui/icons/OpenInBrowser';
import { useMemo } from 'react';
import { useAsync } from 'react-use';
import { formatRelativeTime } from '../../utils/formatRelativeTime';

type Incident = {
  id: string;
  reference: string;
  name: string;
  created_at: string;
  incident_status: { id: string; name: string; category: string };
  incident_role_assignments: Array<{
    role: { role_type: string };
    assignee?: { name: string };
  }>;
  incident_timestamp_values?: Array<{
    incident_timestamp: { name: string };
    value?: { value?: string };
  }>;
  severity?: { name: string; rank?: number };
};
type IncidentsResponse = { incidents: Incident[] };

function IncidentRow({
  baseUrl,
  incident,
}: {
  baseUrl: string;
  incident: Incident;
}) {
  const reportedAt = incident.incident_timestamp_values?.find(ts =>
    ts.incident_timestamp.name.match(/reported/i),
  );
  const reportedAtDate = reportedAt?.value?.value ?? incident.created_at;
  const sinceReported = Date.now() - new Date(reportedAtDate).getTime();
  const sinceReportedLabel = formatRelativeTime(sinceReported);
  const lead = incident.incident_role_assignments.find(a => a.role.role_type === 'lead');

  return (
    <ListItem dense>
      <ListItemText
        primary={
          <>
            <Chip
              size="small"
              label={incident.incident_status.name}
              variant="outlined"
              style={{ marginRight: 8, verticalAlign: 'middle' }}
            />
            {incident.severity?.name && (
              <Chip
                size="small"
                label={incident.severity.name}
                style={{ marginRight: 8, verticalAlign: 'middle' }}
              />
            )}
            {`${incident.reference} ${incident.name}`}
          </>
        }
        primaryTypographyProps={{ variant: 'body1' }}
        secondary={
          <Typography noWrap variant="body2" color="textSecondary">
            Reported {sinceReportedLabel} and{' '}
            {lead?.assignee ? `${lead.assignee.name} is lead` : 'the lead is unassigned'}.
          </Typography>
        }
      />
      <ListItemSecondaryAction>
        <Tooltip title="View in incident.io" placement="top">
          <IconButton
            href={`${baseUrl}/incidents/${incident.id}`}
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
          >
            <OpenInBrowserIcon />
          </IconButton>
        </Tooltip>
      </ListItemSecondaryAction>
    </ListItem>
  );
}

export function IncidentsPage() {
  const config = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const baseUrl = config.getOptionalString('incident.baseUrl') ?? 'https://app.incident.io';
  const proxyPath = config.getOptionalString('incident.proxyPath') ?? '/incident/api';

  const query = useMemo(() => {
    const params = new URLSearchParams();
    params.set('status_category[one_of]', 'active');
    return params.toString();
  }, []);

  const { loading, error, value } = useAsync(async () => {
    const proxyBase = await discoveryApi.getBaseUrl('proxy');
    const res = await fetchApi.fetch(`${proxyBase}${proxyPath}/v2/incidents?${query}`);
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    return res.json() as Promise<IncidentsResponse>;
  }, [discoveryApi, fetchApi, proxyPath, query]);

  const incidents = value?.incidents ?? [];
  const seeMoreHref = `${baseUrl}/incidents?${query}`;

  return (
    <Page themeId="home">
      <Header title="Ongoing Incidents" />
      <Content>
        {loading && <Progress />}
        {error && <Alert severity="error">{error.message}</Alert>}
        {!loading && !error && (
          <>
            {incidents.length > 0 ? (
              <Typography variant="subtitle1">
                There are <strong>{incidents.length}</strong> ongoing incidents.
              </Typography>
            ) : (
              <Typography variant="subtitle1">No ongoing incidents.</Typography>
            )}
            <List dense>
              {incidents.map(incident => (
                <IncidentRow key={incident.id} incident={incident} baseUrl={baseUrl} />
              ))}
            </List>
            <Typography variant="subtitle1">
              Click to{' '}
              <Link target="_blank" rel="noopener noreferrer" href={seeMoreHref}>
                see more.
              </Link>
            </Typography>
          </>
        )}
      </Content>
    </Page>
  );
}
